/**
 * 393 (3) step 1 (ARCHITECTURE §5.50; DECISIONS 393, 404): the woods' density as a **field** of the climate and the relief,
 * 0 … 1 per world-sheet vertex, derived once per planet and wind seed. Today a stand's chance is E5.4's relief alone,
 * calibrated to a fixed dense core a planet; the climate (74.12 step 1) says where a wood could close at all. The field:
 *
 * - **the climate's potential**: the sheet's moisture (the wet mask: drainage, basins, the lee, the archetype's wetness) to
 *   `WOODS.moisturePower`, × a **warmth curve** of the climate's temperature (nothing under `coldFloor` — the treeline and
 *   the poles — full from `warmKnee`, a ramp between: Holdridge's rule, wet and warm closes, dry opens, cold thins), × the
 *   air's share (`thinAir` at no air: a thin air is a dry cold air, 74.10);
 * - **the relief**: E5.4's calibrated chance without the biome's ceiling (core/forestDensity.ts `reliefChance`: valleys,
 *   drainage and the lee up, ridges and the steep down) — one factor among two now, not the whole rule.
 *
 * The biome's ceiling stays the stand's (the forest applies it where it draws the stand, so heath and savanna never
 * close). The planet table draws the field behind the Lab's *Woods density sheet* row. Pure (dmath), cached per sheet,
 * wind seed and person scale. Pass the planet as the forest reads it (its basis: the generated ground), so a crater
 * moves nothing here.
 *
 * **Step 2 — the stands by the field** (core/forest.ts, the density `'climate'`; `WOODS_STANDS`): a stand reads the field
 * at its anchor for its biome (`standClimateAt`: the biome's cold tolerance counted — the firs grow below the broadleaf's
 * floor) and its *fullness*, 0 … 1 from `closedFrom`, drives it — its chance (the ceiling × a floor-to-one ramp), the gap
 * between stands (→ 0), the two stands' exclusion (→ half: the discs overlap and the crowding rule thins the overlaps
 * tree by tree, so the canopy is continuous — the **closed regime**), its trees (× 1 … 5, 64 at most) and spacing (down
 * to a floor a person passes and a vehicle does not, DECISIONS 404 (3)) — the packing on the fullness squared, so
 * parkland stays parkland and only the top closes. The arrangement (404 (4)): in the closed regime the shapes collapse to
 * the disc; at its **edge** (the ecotone) the discs become belts along the field's contour; **clearings** (three seeded noises,
 * `clearings`: many small, few large, a tenth of the ground), **game trails** (a seeded walk on the ground under the route graph's grade rule) and the **creek cut** (no trunk in a drainage line's bed, `bankTrees` on a damp bank) break the closed wood; the **treeline** is the warmth curve's floor, where a
 * stand's trees turn to krummholz. **The rainforest** (430, `WOODS_RAINFOREST`, `woodsRegimeAt`) is the closed regime where the
 * climate is warm and wet: its own mix and 67.61's drawn layers, the old jungle noise retired. Measured and A/B'd in
 * `sweeps/2026-11-01-393-woods-stands.md`.
 *
 * **Step 3 — the rest of the arrangement** (`riparian`, `exposure`, `stand`): a **riparian belt** along the sheet's strong
 * chains (order ≥ `riparianOrder`): within `riparianPeople` of the bank the stands read the field at least
 * `riparianDensity` × the chain's order, whatever the climate says — the line of trees that marks a river in dry country,
 * laid as the contour shape along the bank, the open ground there filled with the strongest biome; and the **windward
 * thinning**: on the flank that faces the prevailing wind (the relief's lee negated, on a slope that counts) the stands
 * read the field × (1 − `windwardThin` × exposure) and their trees turn to snags by `windwardSnags` × exposure. `stand` is
 * the biome-free read with both in (the planet table draws it); `density` stays the climate × the relief.
 */
import type { BiomeId, FloraType, PlanetState, Vec3 } from '../types';
import { worldSheetOf, type WorldSheet } from './worldSheet';
import { climateOf } from './climate';
import { airOf } from './air';
import { reliefWoods, reliefChance, reliefExposure } from './forestDensity';
import { nearestSurfaceVertex } from './surface';
import { dpow, dsin } from './dmath';

export const WOODS = {
  /** the moisture's power in the potential (under 1: a half-wet ground is more than half-wooded) */
  moisturePower: 0.8,
  /** the warmth curve on the climate's 0–1 temperature: no wood under `coldFloor`, full from `warmKnee`, a ramp between */
  coldFloor: 0.04, warmKnee: 0.4,   // (bruma's equatorial lowland reads ~0.2: half a wood, its firs; its poles and summits under the floor: the treeline)
  /** the air's share of the potential at no air (a full air 1) */
  thinAir: 0.5,
  /** the field from which a wood closes (step 2: the stands' gap 0, the canopy continuous) */
  closedFrom: 0.7,
} as const;

/** Step 2: the stands by the field (core/forest.ts under the density `'climate'`). */
export const WOODS_STANDS = {
  /** a stand's chance: its biome's ceiling × (`chanceFloor` + (1 − `chanceFloor`) × its fullness) — a dry world's wood at E5.4's open tenth */
  chanceFloor: 0.1,
  /** the packing — 0 up to this field, then the ramp to `closedFrom` squared (so parkland stays parkland and only the top closes) — drives: a stand's radius from its biome's to `closedRadius` (426: the tiling disc); two stands' exclusion (their radii's sum) × (1 − `overlap` × packing) — in the closed regime the discs overlap and merge (the crowding rule thins the overlaps to one stand's density); the gap between stands × (1 − packing) */
  packFrom: 0.45,
  /**
   * 426 (continuity): in the closed regime the stands tile the cell instead of clumping on open ground — a stand's radius ramps
   * from its biome's to `closedRadius` (people) by the packing, the two stands' exclusion × (1 − `overlap` × packing) reaches 0 at
   * full packing (the crowding rule alone keeps the overlaps at one stand's density), and a closed cell's anchors lie on a
   * jittered lattice (`latticeJitter`: the jitter as a share of the lattice's pitch) rather than at random. Before: the radius
   * × 0.75, the exclusion × 0.25 and random anchors left 3.8 of 8 anchors standing as clumps of ~36 in a radius of 7 — 42 % of a
   * closed cell within 3 people of a trunk; the lattice and the tiling discs give the whole cell (design_sheets/woods_continuity.html).
   */
  closedRadius: 13, overlap: 1, latticeJitter: 0.5,
  /** a stand's trees × (1 + `closedTrees` × packing), 64 at most (426: 2.4 — a broadleaf's 10–16 × 3.4 ≈ 44 laid, ~0.12 stems a square person over a tiled cell after the crowding between the overlapping discs and the clearings' and trails' cut; 2.8 read 0.15 on viridis once 426 (2)'s clearings replaced the old noise's plateau); its spacing × (1 − `closedSpacing` × packing), never under `spacingFloor` (people: a person passes between two trunks, a vehicle does not) */
  closedTrees: 2.4, closedSpacing: 0.55, spacingFloor: 1.5,
  /** the ecotone: from this fullness up to 1 a disc or scatter stand is a belt along the wood's edge (the field's contour, read `edgeSamplePeople` either side) */
  edgeFrom: 0.7, edgeSamplePeople: 30,
  /**
   * 426 (2), the arrangement — clearings many-small-few-large: three value noises on the planet's seed, each `[people across,
   * above]` (the noise's wavelength; the value over which the ground is a clearing — the noise is bell-shaped about 0.5, so
   * 0.82 is its top ~4.5 %, 0.83 ~3.5 %, 0.86 ~2.5 %, read on viridis's and thalia's closed cells); ground any one covers is a clearing — it
   * refuses a stand outside the lattice and cuts a lattice stand's trees. Small ones many (3–5 people across), large ones few
   * (15–25), a tenth of the closed ground in all (the owner, 426: *"high frequency clearings being tiny and overall frequency
   * being low"*). Before: one noise 30 across, stretched ×2.1 and clamped — its top 12 % nominal, a plateau in practice.
   */
  clearings: [[8, 0.82], [16, 0.83], [40, 0.86]] as ReadonlyArray<readonly [number, number]>,
  /**
   * the game trails (core/woodsTrails.ts): `trailChance` of the closed cells start one at their centre — a walk on the ground
   * `trailPeople` long (or to the wood's edge) in steps of `trailStep`, each the cheapest on foot (the route graph's grade
   * rule, constants/action.ts ROUTE) of the headings within ±`trailBend` radians of its own, a bend costing `trailBendCost` a
   * radian, and `trailWander` radians of seeded drift a step (the winding on flat ground); trees within `trailWidth` / 2 of it
   * give way
   */
  trailChance: 0.6, trailPeople: 70, trailStep: 2, trailBend: 0.8, trailBendCost: 0.6, trailWander: 0.4, trailWidth: 1.6,
  /**
   * the creek cut (426 (2), 434: *"creeks along the chains with a bank mix"*), on 74.20's drainage lines (core/drainageLines.ts
   * `drainageAt`, one definition with the beds drawn on them): under 'climate' no trunk stands in a bed (the line's edge ≤ 0),
   * and on a damp bed's bank (0 < edge ≤ the class's bank) a tree is one of `bankTrees` — the weeping willow and the stilt
   * mangrove — by weight; a stony bed's or a wash's bank keeps the stand's own trees
   */
  bankTrees: [['weepingWillow', 3], ['stiltMangrove', 2]] as ReadonlyArray<readonly [FloraType, number]>,
  /** a biome's cold tolerance: it reads the ground this much warmer (the firs below the broadleaf's floor, the birch a little, the heath's krummholz lower still) */
  coldTolerance: { firStand: 0.15, birchGlade: 0.08, heath: 0.12, scrub: 0.05 } as Partial<Record<BiomeId, number>>,
  /** the treeline: under this warmth (the biome's tolerance counted) a stand's trees are krummholz, a few of its own among them */
  treelineBelow: 0.12,
  /** step 3, the riparian belt: within `riparianPeople` of a chain of order ≥ `riparianOrder`, the stands read at least `riparianDensity` × the order */
  riparianOrder: 0.9, riparianPeople: 10, riparianDensity: 0.55,
  /** step 3, the windward flank: the stands read the field × (1 − `windwardThin` × exposure), and `windwardSnags` × exposure of their trees are snags */
  windwardThin: 0.4, windwardSnags: 0.3,
};

/** The field as a stand of `biome` reads it at a direction: its density (the biome's cold tolerance counted; step 3's riparian floor and windward thinning in), the warmth it grows in, the riparian reach and the exposure there. */
export function standClimateAt(planet: PlanetState, windSeed: string, person: number, d: Vec3, biome: BiomeId): { density: number; warmth: number; riparian: number; exposure: number; temperature: number; moisture: number } {
  const sheet = worldSheetOf(planet);
  const v = sheet.graph.nodeOf[nearestSurfaceVertex(planet, d)];
  if (v < 0) return { density: 0, warmth: 0, riparian: 0, exposure: 0, temperature: 0, moisture: 0 };
  const w = woodsDensityOf(planet, windSeed, person), temperature = climateOf(planet, sheet).temperature[v], t = temperature + (WOODS_STANDS.coldTolerance[biome] ?? 0);
  const tol = WOODS_STANDS.coldTolerance[biome] ?? 0;
  const own = tol ? climatePotential(sheet.moisture[v], t, airOf(planet).density) * w.relief[v] : w.density[v];
  // (430: the climate's own temperature and the sheet's moisture too — the rainforest regime reads them, woodsRegimeOf)
  return { density: standRead(own, w.riparian[v], w.exposure[v]), warmth: warmthCurve(t), riparian: w.riparian[v], exposure: w.exposure[v], temperature, moisture: sheet.moisture[v] };
}

/**
 * 430 (DECISIONS 425 (3), 430; design_sheets/rainforest.html): **the rainforest** is the woods field's warm, wet closed regime —
 * not a biome and not a dial: where a stand's field reads closed (its fullness 1) on ground whose climate temperature is at
 * least `warmFrom` (the wet worlds' tropics: viridis's base 0.72, pelago's 0.64, thalia's 0.6 at the equator's lowland, less
 * the latitude's, the elevation's and the thin air's cooling; the climate table's *hot* band is the dry worlds' and would
 * never grow one) and whose sheet moisture is at least `wetFrom`, the stand draws `trees` — the kapok, the strangler fig and
 * the bamboo clump with the kit's frond palm, tree fern and stilt mangrove, a snag now and then — instead of its biome's (as
 * the treeline swaps a stand's for krummholz), keyed on one read, `woodsRegimeAt`. At its **edge** (the fullness from
 * `WOODS_STANDS.edgeFrom` up to 1, warm and wet) the mix is drawn too, so the species walk out of the jungle instead of
 * stopping at a line. 447 (the owner, Nov 4: the canopy *"floats over dense trees and has vines go down, but has no trunks
 * tall enough to reach the canopy"*): 67.61's drawn layers (the canopy fill, ferns, bushes, vines) and their depth field are
 * removed — the rainforest's look and cover are its trees' own, as every other wood's. The old jungle (67.61: `jungleAt`'s own noise over the wooded biomes, cold or hot, `GameConfig.forestJungle`, the Lab's
 * Jungle row) is retired: the regime adds no stems — a rainforest's are the closed regime's (0.12 a square person, 426) — it
 * changes the species. Shares of the sheet's vertices (lab-7 megaplanets): viridis 17 %, pelago 9.5 %,
 * thalia 7 %; `warmFrom` is the one dial that moves them (0.6 reads viridis 10 %), the owner's to tighten (430) if the cost asks.
 */
export const WOODS_RAINFOREST = {
  warmFrom: 0.5, wetFrom: 0.6,
  trees: [['kapok', 1], ['stranglerFig', 1], ['frondPalm', 2], ['treeFern', 2], ['stiltMangrove', 1], ['bambooClump', 1], ['snag', 0.3]] as ReadonlyArray<readonly [FloraType, number]>,
};
/** The woods' regime at a place: open ground, parkland, the wood's edge (the ecotone), the closed wood — and, warm and wet, the rainforest and its edge. */
export type WoodsRegime = 'open' | 'parkland' | 'edge' | 'closed' | 'rainforestEdge' | 'rainforest';
/** The regime from a stand's fullness (standFullness; a lattice cell's anchors read 1), the climate's temperature and the sheet's moisture there. */
export function woodsRegimeOf(fullness: number, temperature: number, moisture: number): WoodsRegime {
  if (fullness <= 0) return 'open';
  if (fullness < WOODS_STANDS.edgeFrom) return 'parkland';
  const warmWet = temperature >= WOODS_RAINFOREST.warmFrom && moisture >= WOODS_RAINFOREST.wetFrom;
  if (fullness >= 1) return warmWet ? 'rainforest' : 'closed';
  return warmWet ? 'rainforestEdge' : 'edge';
}
/** Whether a regime is the rainforest's (its mix, its layers): the closed regime or its edge, warm and wet. */
export function isRainforest(regime: WoodsRegime): boolean { return regime === 'rainforest' || regime === 'rainforestEdge'; }
/** The regime at a direction, biome-free (the stands' read of the field, the climate's temperature, the sheet's moisture at the nearest vertex; 'open' off the sheet): the table's and the sheets' read; a stand's own is its field's (core/forest.ts forestStandRegime, a lattice cell closed as a whole). */
export function woodsRegimeAt(planet: PlanetState, windSeed: string, person: number, d: Vec3): WoodsRegime {
  const sheet = worldSheetOf(planet);
  const v = sheet.graph.nodeOf[nearestSurfaceVertex(planet, d)];
  if (v < 0) return 'open';
  return woodsRegimeOf(standFullness(woodsDensityOf(planet, windSeed, person).stand[v]), climateOf(planet, sheet).temperature[v], sheet.moisture[v]);
}

/** A stand's fullness from the field: 0 … 1, 1 from `closedFrom` (the closed regime) — its chance, the wood's edge and the fill read it. */
export function standFullness(density: number): number { return Math.max(0, Math.min(1, density / WOODS.closedFrom)); }
/** A stand's packing from the field: 0 up to `packFrom`, the ramp to `closedFrom` squared — its radius, exclusion, gap, trees and spacing read it. */
export function standPacking(density: number): number {
  const t = Math.max(0, Math.min(1, (density - WOODS_STANDS.packFrom) / (WOODS.closedFrom - WOODS_STANDS.packFrom)));
  return t * t;
}

export interface WoodsDensity {
  /** per sheet vertex: the climate's potential, the relief's chance, and the field (their product) */
  potential: Float32Array; relief: Float32Array; density: Float32Array;
  /** step 3, per sheet vertex: the riparian reach (the strongest chain's order within `riparianPeople`, 0 off the banks), the windward exposure (0 … 1), and the stands' read of the field with both in (biome-free: `standRead`) */
  riparian: Float32Array; exposure: Float32Array; stand: Float32Array;
  /** the world's: the field's mean, and the share of its vertices at or past `closedFrom` */
  mean: number; closedShare: number;
}

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));

/** The warmth curve: 0 under the cold floor, 1 from the warm knee, a ramp between. */
export function warmthCurve(temperature: number): number {
  return clamp01((temperature - WOODS.coldFloor) / (WOODS.warmKnee - WOODS.coldFloor));
}

/** The climate's potential for a closed wood at a vertex: moisture, warmth and the air, each 0–1. */
export function climatePotential(moisture: number, temperature: number, airDensity: number): number {
  const m = clamp01(moisture);
  return (m > 0 ? dpow(m, WOODS.moisturePower) : 0) * warmthCurve(temperature) * (WOODS.thinAir + (1 - WOODS.thinAir) * clamp01(airDensity));
}

const cache = new WeakMap<WorldSheet, Map<string, WoodsDensity>>();

/** The planet's woods density field (built on first ask; cached per sheet, wind seed and person). */
export function woodsDensityOf(planet: PlanetState, windSeed: string, person: number): WoodsDensity {
  const sheet = worldSheetOf(planet);
  let byKey = cache.get(sheet);
  if (!byKey) { byKey = new Map(); cache.set(sheet, byKey); }
  const key = `${windSeed}|${person}`;
  const hit = byKey.get(key);
  if (hit) return hit;
  const g = sheet.graph, V = g.count;
  const climate = climateOf(planet, sheet), air = airOf(planet).density, relief = reliefWoods(planet, windSeed, person);
  const potential = new Float32Array(V), rel = new Float32Array(V), density = new Float32Array(V), exposure = new Float32Array(V), stand = new Float32Array(V);
  let sum = 0, closed = 0;
  for (let v = 0; v < V; v++) {
    const d: Vec3 = { x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] };
    potential[v] = climatePotential(sheet.moisture[v], climate.temperature[v], air);
    rel[v] = reliefChance(planet, relief, d);
    density[v] = potential[v] * rel[v];
    exposure[v] = reliefExposure(planet, relief, d);
    sum += density[v];
    if (density[v] >= WOODS.closedFrom) closed++;
  }
  const riparian = riparianReach(sheet, planet.radius / person);
  for (let v = 0; v < V; v++) stand[v] = standRead(density[v], riparian[v], exposure[v]);
  const out: WoodsDensity = { potential, relief: rel, density, riparian, exposure, stand, mean: V ? sum / V : 0, closedShare: V ? closed / V : 0 };
  byKey.set(key, out);
  return out;
}

/** Step 3: the riparian reach per sheet vertex — the strongest chain's order (≥ `riparianOrder`) whose bank lies within `riparianPeople` (a walk over the graph from each chain's vertices, the arc by the chord; arithmetic only). `R` the planet's radius in people. */
function riparianReach(sheet: WorldSheet, R: number): Float32Array {
  const g = sheet.graph, V = g.count, out = new Float32Array(V);
  const half = dsin(Math.min(Math.PI, WOODS_STANDS.riparianPeople / R) / 2), lim2 = 4 * half * half;   // (a chord's test, no arcsine: the same on every engine)
  const stamp = new Int32Array(V).fill(-1), queue = new Int32Array(V);
  const chains = sheet.chains.map((c, i) => ({ c, i })).filter(({ c }) => c.order >= WOODS_STANDS.riparianOrder).sort((a, b) => a.c.order - b.c.order || a.i - b.i);   // (ascending: the strongest writes last)
  for (const { c, i } of chains) {
    const mark = i;
    for (const s of c.vertices) {
      const cx = g.dirs[3 * s], cy = g.dirs[3 * s + 1], cz = g.dirs[3 * s + 2];
      let head = 0, tail = 0;
      queue[tail++] = s; stamp[s] = mark; out[s] = Math.max(out[s], c.order);
      while (head < tail) {
        const u = queue[head++];
        for (let k = g.start[u], e = g.start[u + 1]; k < e; k++) {
          const w = g.nbr[k];
          if (stamp[w] === mark) continue;
          const dx = cx - g.dirs[3 * w], dy = cy - g.dirs[3 * w + 1], dz = cz - g.dirs[3 * w + 2];
          if (dx * dx + dy * dy + dz * dz > lim2) continue;
          stamp[w] = mark; queue[tail++] = w; out[w] = Math.max(out[w], c.order);
        }
      }
      // (the next bank vertex's walk re-reaches this one's: the stamp is per chain, so a vertex within reach of two of its
      // vertices is written once — the same order either way)
      for (let q = 0; q < tail; q++) stamp[queue[q]] = -1;
    }
  }
  return out;
}

/** Step 3: the stands' read of the field at a vertex — the riparian floor over the field, then the windward thinning. */
export function standRead(density: number, riparian: number, exposure: number): number {
  return Math.max(density, riparian * WOODS_STANDS.riparianDensity) * (1 - WOODS_STANDS.windwardThin * exposure);
}

/** The stands' read of the field at a direction, biome-free (the fill and the edge read it): its nearest sheet vertex's (0 off the sheet). */
export function woodsStandAt(planet: PlanetState, windSeed: string, person: number, d: Vec3): number {
  const sheet = worldSheetOf(planet);
  const v = sheet.graph.nodeOf[nearestSurfaceVertex(planet, d)];
  return v >= 0 ? woodsDensityOf(planet, windSeed, person).stand[v] : 0;
}

/** The field at a direction: its nearest sheet vertex's (0 off the sheet). */
export function woodsDensityAt(planet: PlanetState, windSeed: string, person: number, d: Vec3): number {
  const sheet = worldSheetOf(planet);
  const v = sheet.graph.nodeOf[nearestSurfaceVertex(planet, d)];
  return v >= 0 ? woodsDensityOf(planet, windSeed, person).density[v] : 0;
}

/** The table's colour of the field (the Lab's Woods density sheet row): pale sand at 0 through olive to a deep green at 1. */
export function woodsDensityColor(x: number): string {
  const t = clamp01(x);
  const r = Math.round(214 - 170 * t), g = Math.round(200 - 90 * t), b = Math.round(160 - 120 * t);
  return `rgba(${r},${g},${b},0.55)`;
}
